// hooks/api/softSkillVendor.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as softSkillVendorService from "@/services/softSkillVendor.service";
import {
  SoftSkillVendorCreateType,
  SoftSkillVendorParamsType,
  SoftSkillVendorType,
} from "@/services/softSkillVendor.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

export const softSkillVendorsKey = {
  all: ["softSkillVendors"] as const,
  lists: (filters: SoftSkillVendorParamsType) =>
    [...softSkillVendorsKey.all, "lists", filters] as const,
  detail: (id: string) => [...softSkillVendorsKey.all, "detail", id] as const,
};

export const useSoftSkillVendorsQuery = (filters: SoftSkillVendorParamsType) =>
  useQuery({
    queryKey: softSkillVendorsKey.lists(filters),
    queryFn: () => softSkillVendorService.fetchAllSoftSkillVendors(filters),
    placeholderData: (prev) => prev,
  });

export const useSoftSkillVendorByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: softSkillVendorsKey.detail(id),
    queryFn: () => softSkillVendorService.fetchSoftSkillVendorById(id),
    enabled: enabled && !!id,
  });

export const useCreateSoftSkillVendorMutation = (
  filters: SoftSkillVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation<
    SoftSkillVendorType,
    AxiosError,
    SoftSkillVendorCreateType
  >({
    mutationFn: (data) => softSkillVendorService.createSoftSkillVendor(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: softSkillVendorsKey.lists(filters),
      });
      notifySuccess("Soft skill vendor added successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to add soft skill vendor"
      ),
  });
};

export const useUpdateSoftSkillVendorMutation = (
  filters: SoftSkillVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      _id,
      data,
    }: {
      _id: string;
      data: SoftSkillVendorCreateType;
    }) => softSkillVendorService.updateSoftSkillVendor(_id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: softSkillVendorsKey.lists(filters),
      });
      queryClient.invalidateQueries({
        queryKey: softSkillVendorsKey.detail(variables._id),
      });
      notifySuccess("Soft skill vendor updated successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to update soft skill vendor"
      ),
  });
};

export const useDeleteSoftSkillVendorMutation = (
  filters: SoftSkillVendorParamsType
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (_id: string) =>
      softSkillVendorService.deleteSoftSkillVendor(_id),
    onSuccess: (_, _id) => {
      queryClient.invalidateQueries({
        queryKey: softSkillVendorsKey.lists(filters),
      });
      queryClient.removeQueries({ queryKey: softSkillVendorsKey.detail(_id) });
      notifySuccess("Soft skill vendor deleted successfully");
    },
    onError: (error: any) =>
      notifyError(
        error?.response?.data?.message || "Failed to delete soft skill vendor"
      ),
  });
};
